export type HomeTimePeriod = 'predawn' | 'morning' | 'midday' | 'evening' | 'night';

// 홈 hero의 선택지와 시간대 규칙은 구성원 Home 경험의 일부다.
export type HomeHeroThemeId =
  | 'seoul'
  | 'space'
  | 'nature'
  | 'ocean'
  | 'mountain'
  | 'bridge'
  | 'autonomous';

type FixedTheme = {
  mode: 'time';
  images: Record<HomeTimePeriod, string>;
};

type HourlyTheme = {
  mode: 'hourly-random';
  images: readonly string[];
};

type PeriodRandomTheme = {
  mode: 'period-random';
  images: Record<HomeTimePeriod, readonly string[]>;
};

export type HomeHeroTheme = {
  id: HomeHeroThemeId;
  label: string;
  description: string;
  preview: string;
  selectionLabel: string;
} & (FixedTheme | HourlyTheme | PeriodRandomTheme);

// 이미지가 로드되기 전에도 hero 가장자리 색이 이어지도록 asset별 배경색을 함께 관리한다.
const periodFallbacks: Record<HomeTimePeriod, string> = {
  predawn: '#33486F',
  morning: '#D9F1F3',
  midday: '#8ED9F0',
  evening: '#E8A488',
  night: '#0A294C',
};

const periodGreetings: Record<HomeTimePeriod, string> = {
  predawn: '좋은 새벽이에요',
  morning: '좋은 아침이에요',
  midday: '좋은 오후예요',
  evening: '좋은 저녁이에요',
  night: '편안한 밤이에요',
};

const themeImage = (theme: string, name: string) => `/images/home/themes/${theme}/${name}.webp`;

export const homeHeroThemes: readonly HomeHeroTheme[] = [
  {
    id: 'seoul',
    label: '서울',
    description: '한강과 서울의 하루',
    selectionLabel: '시간대에 따라 바뀌어요',
    preview: '/images/home/seoul-morning-12x1-v4.webp',
    mode: 'time',
    images: {
      predawn: '/images/home/seoul-predawn-12x1-v4.webp',
      morning: '/images/home/seoul-morning-12x1-v4.webp',
      midday: '/images/home/seoul-midday-12x1-v4.webp',
      evening: '/images/home/seoul-evening-12x1-v4.webp',
      night: '/images/home/seoul-night-12x1-v4.webp',
    },
  },
  {
    id: 'space',
    label: '우주',
    description: '우주 출근길과 사무실',
    selectionLabel: '시간대에 따라 바뀌어요',
    preview: themeImage('space', 'space-commute-morning'),
    mode: 'time',
    images: {
      predawn: themeImage('space', 'space-office-predawn'),
      morning: themeImage('space', 'space-commute-morning'),
      midday: themeImage('space', 'space-office-day'),
      evening: themeImage('space', 'space-office-evening'),
      night: themeImage('space', 'space-office-night'),
    },
  },
  {
    id: 'nature',
    label: '자연',
    description: '숲, 들판, 습지의 풍경',
    selectionLabel: '매시간 새로운 풍경',
    preview: themeImage('nature', 'nature-01'),
    mode: 'hourly-random',
    images: Array.from({ length: 10 }, (_, index) => themeImage('nature', `nature-${String(index + 1).padStart(2, '0')}`)),
  },
  {
    id: 'ocean',
    label: '바다',
    description: '수평선으로 흐르는 하루',
    selectionLabel: '시간대에 따라 바뀌어요',
    preview: themeImage('ocean', 'ocean-midday'),
    mode: 'time',
    images: {
      predawn: themeImage('ocean', 'ocean-predawn'),
      morning: themeImage('ocean', 'ocean-morning'),
      midday: themeImage('ocean', 'ocean-midday'),
      evening: themeImage('ocean', 'ocean-evening'),
      night: themeImage('ocean', 'ocean-night'),
    },
  },
  {
    id: 'mountain',
    label: '산',
    description: '시간대별 산과 능선',
    selectionLabel: '현재 시간대에서 랜덤',
    preview: themeImage('mountain', 'mountain-midday-01'),
    mode: 'period-random',
    images: periodImagePairs('mountain'),
  },
  {
    id: 'bridge',
    label: '다리',
    description: '도시를 잇는 여러 다리',
    selectionLabel: '현재 시간대에서 랜덤',
    preview: themeImage('bridge', 'bridge-evening-01'),
    mode: 'period-random',
    images: periodImagePairs('bridge'),
  },
  {
    id: 'autonomous',
    label: '자율주행',
    description: '자율주행 도시의 하루',
    selectionLabel: '시간대에 따라 장면이 이어져요',
    preview: themeImage('autonomous', 'autonomous-morning'),
    mode: 'time',
    images: {
      predawn: themeImage('autonomous', 'autonomous-predawn'),
      morning: themeImage('autonomous', 'autonomous-morning'),
      midday: themeImage('autonomous', 'autonomous-midday'),
      evening: themeImage('autonomous', 'autonomous-evening'),
      night: themeImage('autonomous', 'autonomous-night'),
    },
  },
] as const;

export const defaultHomeHeroThemeId: HomeHeroThemeId = 'seoul';

export function isHomeHeroThemeId(value: string | null): value is HomeHeroThemeId {
  return homeHeroThemes.some((theme) => theme.id === value);
}

export function getHomeTimePeriod(hour: number): HomeTimePeriod {
  if (hour >= 4 && hour < 7) return 'predawn';
  if (hour >= 7 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 17) return 'midday';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

export function getHomeHero(themeId: HomeHeroThemeId, now: Date, viewerKey: string) {
  const period = getHomeTimePeriod(now.getHours());
  const theme = homeHeroThemes.find((candidate) => candidate.id === themeId) ?? homeHeroThemes[0];
  const hourlySeed = `${viewerKey}:${theme.id}:${localHourKey(now)}`;
  let image: string;

  if (theme.mode === 'time') {
    image = theme.images[period];
  } else if (theme.mode === 'hourly-random') {
    image = pickDeterministically(theme.images, hourlySeed);
  } else {
    image = pickDeterministically(theme.images[period], `${hourlySeed}:${period}`);
  }

  return {
    period,
    theme,
    image,
    greeting: periodGreetings[period],
    fallback: periodFallbacks[period],
  };
}

function periodImagePairs(theme: 'mountain' | 'bridge'): Record<HomeTimePeriod, readonly string[]> {
  return {
    predawn: [themeImage(theme, `${theme}-predawn-01`), themeImage(theme, `${theme}-predawn-02`)],
    morning: [themeImage(theme, `${theme}-morning-01`), themeImage(theme, `${theme}-morning-02`)],
    midday: [themeImage(theme, `${theme}-midday-01`), themeImage(theme, `${theme}-midday-02`)],
    evening: [themeImage(theme, `${theme}-evening-01`), themeImage(theme, `${theme}-evening-02`)],
    night: [themeImage(theme, `${theme}-night-01`), themeImage(theme, `${theme}-night-02`)],
  };
}

function localHourKey(date: Date) {
  return [date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours()].join('-');
}

function pickDeterministically(images: readonly string[], seed: string) {
  return images[stableHash(seed) % images.length];
}

function stableHash(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
